import { defineMessages, useIntl } from "react-intl";

import {
  queryClient,
  QueryKeys,
  MutationActions,
  useEntityMutation,
} from "@/api/core";
import { updatePredictionStatusCache } from "@/api/predictions/utils";
import { useAdjust } from "@/contexts/adjust-context";
import { useApi } from "@/hooks";
import { statusSchema } from "@/schemas";
import toast from "@/toast";

import { updateStatusCache } from "../utils";

const messages = defineMessages({
  deletedTruth: {
    id: "status.error.deletedTruth",
    defaultMessage: "This Truth has been deleted and is no longer available.",
  },
});

interface Variables {
  id: string;
}

function useReblogStatus() {
  const intl = useIntl();
  const api = useApi();
  const { trackEngagement } = useAdjust();

  return useEntityMutation({
    globalQueryKey: QueryKeys.statuses.global,
    idAttributeFromVariables: "id",
    action: MutationActions.UPDATE,
    skipOnSuccess: true,
    schema: statusSchema,
    mutationFn: (variables: Variables) =>
      api.put(`/api/v1/statuses/${variables.id}/reblog`),
    onMutate(variables) {
      const [prevStatus] = updateStatusCache(variables.id, (prevStatus) => ({
        reblogged: true,
        reblogs_count: prevStatus.reblogs_count + 1,
      }));
      const [prevPredictionStatus] = updatePredictionStatusCache(
        variables.id,
        (prevStatus) => ({
          reblogged: true,
          reblogs_count: prevStatus.reblogs_count + 1,
        }),
      );

      trackEngagement("retruth");

      return { prevPredictionStatus, prevStatus };
    },
    onError(error, variables, context) {
      if (error.response?.status === 404) {
        toast.error(intl.formatMessage(messages.deletedTruth));
      }
      if (context?.prevStatus) {
        queryClient.setQueryData(
          QueryKeys.statuses.detail(variables.id),
          context.prevStatus,
        );
      }
      if (context?.prevPredictionStatus) {
        queryClient.setQueryData(
          QueryKeys.predictions.prediction(variables.id),
          context.prevPredictionStatus,
        );
      }
    },
  });
}

export { useReblogStatus };
